import React, { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Surface } from '../ui/Surface';

export interface AgentCardData {
  id: string;
  instanceId?: string;
  name: string;
  description?: string;
  departmentId?: string;
  deletedAt?: string | null;
  metadata?: {
    agentRole?: 'entry' | 'functional';
    entryType?: string;
  };
}

interface AgentCardProps {
  agent: AgentCardData;
  banner: string;
  departmentName: string;
  onEdit: (agent: AgentCardData) => void;
  onDelete: (id: string) => void;
}

const FALLBACK_DESCRIPTION = 'Plans, drafts, and schedules posts across all your channels effortlessly.';

// Traces just the notch Figma cuts into the banner's bottom edge for the open-agent icon
// button: a flat plateau (x 290-316) with 18px quarter-circle transitions down to the
// banner's baseline at x=254/352. Only the hole itself is filled white (revealing the card's
// white background) — the rest of the strip is left transparent so the banner photo shows
// through normally. viewBox matches the banner's real pixel size (400x144) so
// preserveAspectRatio="none" keeps it aligned as the card's rendered width varies.
const BANNER_NOTCH_PATH =
  'M352 42C342.059 42 334 33.941 334 24V18C334 8.059 325.941 0 316 0H290C280.059 0 272 8.059 272 18V24C272 33.941 263.941 42 254 42H352Z';

// Cuts the card's own outline (rounded rect minus the bottom-right notch that the
// Configure button sits flush in) so the border stops there instead of boxing the
// button in — matching Figma, where Configure reads as a separate floating element.
// Expressed as fractions of the card's own box (objectBoundingBox) so it scales with
// the card's actual rendered width, derived from Figma's exact boolean-subtract path.
const CARD_NOTCH_CLIP_PATH =
  'M 0.95318 0.00086 C 0.97849 0.00086 0.99900 0.01867 0.99900 0.04064 V0.83537 C 0.99900 0.85686 0.97894 0.87428 0.95418 0.87428 H0.70020 C 0.67490 0.87428 0.65438 0.89208 0.65438 0.91405 V0.96022 C 0.65438 0.98171 0.63432 0.99914 0.60956 0.99914 H0.04681 C 0.02151 0.99914 0.00100 0.98133 0.00100 0.95936 V0.04064 C 0.00100 0.01867 0.02151 0.00086 0.04681 0.00086 H0.95318 Z';

export const AgentCard: React.FC<AgentCardProps> = ({ agent, banner, departmentName, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const agentId = agent.instanceId || agent.id;
  const clipId = `agent-card-clip-${useId()}`;

  const goToConfig = () => navigate(`/agents/${agentId}/config`);

  return (
    <article
      className={`group relative h-[462px] w-full max-w-[400px] ${agent.deletedAt ? 'opacity-55' : ''}`}
    >
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d={CARD_NOTCH_CLIP_PATH} />
          </clipPath>
        </defs>
      </svg>

      {/* Clipped to a rounded rect with the bottom-right corner cut away, so the border
          stops short of the Configure button below rather than boxing it in. */}
      <Surface
        className="absolute inset-0 flex h-full w-full flex-col rounded-[18px] border border-c-e3e7f3 bg-white"
        style={{ clipPath: `url(#${clipId})` }}
      >
        <div className="relative h-[144px] shrink-0 bg-cover bg-center" style={{ backgroundImage: `url(${banner})` }}>
          <svg
            viewBox="0 0 400 42"
            preserveAspectRatio="none"
            className="absolute inset-x-0 bottom-0 h-[42px] w-full"
            aria-hidden
          >
            <path d={BANNER_NOTCH_PATH} fill="#FFFFFF" />
          </svg>

          <Button tone="custom"
            type="button"
            onClick={goToConfig}
            className="absolute right-[calc(24.05%-26px)] top-[108px] z-10 flex h-[52px] w-[52px] items-center justify-center rounded-[12px] border border-c-3683c6 bg-white text-c-1f1f1f"
            aria-label="Open agent"
          >
            <ArrowUpRight className="h-6 w-6" strokeWidth={1.75} />
          </Button>

          <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
            <Button tone="custom"
              type="button"
              onClick={() => onEdit(agent)}
              className="rounded-lg border border-white/40 bg-white/90 px-3 py-2 text-xs font-medium text-c-414865 backdrop-blur-sm"
            >
              Edit
            </Button>

            {!agent.deletedAt && (
              <Button tone="custom"
                type="button"
                onClick={() => onDelete(agentId)}
                className="rounded-lg border border-white/40 bg-white/90 px-3 py-2 text-xs font-medium text-rose-600 backdrop-blur-sm"
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-1 flex-col px-5 pb-0 pt-[26px]">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-[2px]">
              <h3 className="line-clamp-1 text-[24px] font-semibold leading-[1.25] text-c-1f1f1f">{agent.name}</h3>
              <p className="text-[15px] font-light leading-[1.2] text-c-808080">{departmentName}</p>
            </div>
            <p className="line-clamp-2 text-[16px] font-light leading-[1.25] text-c-1f1f1f">
              {agent.description || FALLBACK_DESCRIPTION}
            </p>
          </div>

         {/* <div className="mt-[23px] grid grid-cols-2 gap-4">
            <div className="h-[60px] rounded-[12px] bg-c-f5f7fa" />
            <div className="h-[60px] rounded-[12px] bg-c-f5f7fa" />
          </div> */}

          {agent.metadata?.agentRole && (
            <div
              className={`mt-2 flex items-center gap-1 self-start rounded-full border px-2.5 py-1 text-xs font-medium ${
                agent.metadata.agentRole === 'entry'
                  ? 'border-purple-200 bg-purple-50 text-purple-700'
                  : 'border-green-200 bg-green-50 text-green-700'
              }`}
            >
              {agent.metadata.agentRole === 'entry' ? 'Entry' : 'Functional'}
              {agent.metadata.agentRole === 'entry' && agent.metadata.entryType && (
                <span className="ml-1 opacity-70">· {agent.metadata.entryType}</span>
              )}
            </div>
          )}
        </div>
      </Surface>

      <Button tone="brand-gradient"
        type="button"
        onClick={goToConfig}
        className="absolute bottom-0 right-0 z-10 flex h-[40px] w-[124px] items-center justify-center gap-[10px] rounded-[10px] px-3 text-[16px] font-normal"
      >
        Configure
        <ArrowUpRight className="h-5 w-5" strokeWidth={1.75} />
      </Button>
    </article>
  );
};

export default AgentCard;
